import { Token } from '@domain/tokens'
import { Address } from 'viem'
import { FundState } from '@domain/funds/types/FundState.enum.ts'
import { FundDetails } from '@domain/funds/types/FundDetails.ts'
import { FundFilterType } from './types/FundFilterType'

export type Fund = {
  address: Address
  details: FundDetails
  investmentNftValue: bigint
  availableFunds: bigint
  userTotalWithdrawal: bigint
  token: Token
  fundFilterType?: FundFilterType
}

export enum FundStage {
  Proposed = 'Proposed',
  New = 'New',
  Whitelisted = 'Whitelisted',
  Vesting = 'Vesting',
  Closed = 'Closed',
  Upcoming = 'Upcoming',
  ComingSoon = 'Coming Soon',
  Active = 'Active',
  Inactive = 'Inactive',
  Paused = 'Paused',
}

export const isUpcomingStage = (stage?: string | null) =>
  stage?.toLowerCase() === FundStage.Upcoming.toLowerCase()

export const isComingSoonStage = (stage?: string | null) =>
  stage?.toLowerCase() === FundStage.ComingSoon.toLowerCase()

export const isUpcomingOrComingSoonStage = (stage?: string | null) =>
  isUpcomingStage(stage) || isComingSoonStage(stage)

export const isWhitelistedStage = (stage?: string | null) =>
  stage?.toLowerCase() === FundStage.Whitelisted.toLowerCase()

/** Frontend display label for fund stage (Strapi value stays `Whitelisted`). */
export const getFundStageDisplayLabel = (stage?: string | FundStage | null): string => {
  if (!stage) return ''
  if (isWhitelistedStage(stage)) return 'Whitelist'
  return stage
}

export const isOpenPreIpoFundStage = (stage?: string | null) =>
  stage?.toLowerCase() === FundStage.New.toLowerCase() || isWhitelistedStage(stage)

export const resolvePreIpoFundStage = (stage?: string | null): FundStage => {
  const normalized = stage?.toLowerCase()
  if (normalized === FundStage.New.toLowerCase()) return FundStage.New
  if (isWhitelistedStage(stage)) return FundStage.Whitelisted
  if (normalized === FundStage.Paused.toLowerCase()) return FundStage.Paused
  return FundStage.Closed
}

export const fundStateMeta: Record<
  FundState,
  { status: FundStage; description: string }
> = {
  [FundState.FundsIn]: {
    status: FundStage.New,
    description: 'You can invest in this Fund now',
  },
  [FundState.CapReached]: {
    status: FundStage.New,
    description: 'Waiting for the Fund to be deployed',
  },
  [FundState.FundsDeployed]: {
    status: FundStage.Vesting,
    description: 'Funds have been distributed, now vesting is in progress',
  },
  [FundState.Breakeven]: {
    status: FundStage.Vesting,
    description: 'Funds have been distributed, now vesting is in progress',
  },
  [FundState.Closed]: {
    status: FundStage.Closed,
    description: 'All profit has been distributed, you can’t invest anymore',
  },
}
